// Toaster.jsx — global toast notification renderer.
// Mount once at the root of the app (inside App.jsx).
// Dispatches are triggered via the toast singleton in src/lib/toast.js.

import React, { useEffect, useReducer, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';
import { _registerDispatch } from '../lib/toast';

let _id = 0;

function reducer(state, action) {
  switch (action.type) {
    case 'ADD':
      return [...state, { id: ++_id, ...action.payload }];
    case 'REMOVE':
      return state.filter((t) => t.id !== action.id);
    default:
      return state;
  }
}

const STYLES = {
  error:   { bg: 'bg-red-50 dark:bg-red-900/30',     border: 'border-red-200 dark:border-red-700',     text: 'text-red-800 dark:text-red-200',     Icon: AlertCircle,    iconCls: 'text-red-500' },
  success: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', border: 'border-emerald-200 dark:border-emerald-700', text: 'text-emerald-800 dark:text-emerald-200', Icon: CheckCircle,  iconCls: 'text-emerald-500' },
  info:    { bg: 'bg-blue-50 dark:bg-blue-900/30',    border: 'border-blue-200 dark:border-blue-700',   text: 'text-blue-800 dark:text-blue-200',   Icon: Info,           iconCls: 'text-blue-500' },
  warning: { bg: 'bg-amber-50 dark:bg-amber-900/30',  border: 'border-amber-200 dark:border-amber-700', text: 'text-amber-800 dark:text-amber-200',  Icon: AlertTriangle,  iconCls: 'text-amber-500' },
};

function Toast({ id, message, type = 'error', duration = 5000, onRemove }) {
  const s = STYLES[type] || STYLES.error;
  const { Icon } = s;

  useEffect(() => {
    const t = setTimeout(() => onRemove(id), duration);
    return () => clearTimeout(t);
  }, [id, duration, onRemove]);

  return (
    <div
      className={`flex items-start gap-3 w-full max-w-sm rounded-xl border px-4 py-3 shadow-lg pointer-events-auto transition-all ${s.bg} ${s.border}`}
      role="alert"
    >
      <Icon size={16} className={`flex-shrink-0 mt-0.5 ${s.iconCls}`} />
      <p className={`flex-1 text-sm font-medium leading-snug ${s.text}`}>{message}</p>
      <button
        onClick={() => onRemove(id)}
        className={`flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity ${s.text}`}
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export default function Toaster() {
  const [toasts, dispatch] = useReducer(reducer, []);
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  useEffect(() => {
    _registerDispatch((payload) => {
      dispatchRef.current({ type: 'ADD', payload });
    });
    return () => _registerDispatch(null);
  }, []);

  const remove = (id) => dispatch({ type: 'REMOVE', id });

  if (!toasts.length) return null;

  return createPortal(
    <div
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
    >
      {toasts.map((t) => (
        <Toast key={t.id} {...t} onRemove={remove} />
      ))}
    </div>,
    document.body,
  );
}
